// REST fallback for the assigned driver's coordinates, used when the SignalR
// socket is stale/dead. Mirrors flutter_client getCurrentDriverCoordinate:
//   GET /client/searchdriver/{driverId}/location  -> { data: { latitude, longitude } }
// The reference polls this every 1.5s and skips the HTTP tick whenever the
// socket is fresh (see order_repository_impl.dart pollCurrentDriverCoordinates).

import { request } from './http';
import type { LatLon, NearbyDriver } from '../domain/types';

type CoordinatesEnvelope = { data?: { latitude?: number | null; longitude?: number | null } };

export async function getDriverLocation(driverId: string): Promise<LatLon | null> {
  const res = await request<CoordinatesEnvelope>(
    `/client/searchdriver/${encodeURIComponent(driverId)}/location`,
  );
  const coord = res.data;
  if (!coord) return null;
  const lat = coord.latitude;
  const lon = coord.longitude;
  if (typeof lat !== 'number' || typeof lon !== 'number') return null;
  // (0,0) means the server doesn't know the position yet — treat as unknown.
  if (lat === 0 && lon === 0) return null;
  return { lat, lon };
}

type NearbyRaw = {
  driverId?: string;
  latitude?: number;
  longitude?: number;
  driverPhoto?: string | null;
  carPhoto?: string | null;
  carColor?: string | null;
};

type NearbyEnvelope = { data?: NearbyRaw[] };

// GET /client/searchdriver/nearby-drivers?lat=&lon=&count=&radius= — free cars
// around the pickup, shown on the map while building/searching. Mirrors
// flutter_client searchAllDrivers (pollAllDrivers, 3s, count 10, radius 5km).
export async function getNearbyDrivers(lat: number, lon: number): Promise<NearbyDriver[]> {
  const url =
    `/client/searchdriver/nearby-drivers?lat=${lat}&lon=${lon}` +
    `&count=10&radius=5000`;
  const res = await request<NearbyEnvelope>(url);
  const list = Array.isArray(res.data) ? res.data : [];
  const out: NearbyDriver[] = [];
  for (const d of list) {
    if (!d.driverId) continue;
    if (typeof d.latitude !== 'number' || typeof d.longitude !== 'number') continue;
    if (d.latitude === 0 && d.longitude === 0) continue;
    out.push({
      driverId: d.driverId,
      lat: d.latitude,
      lon: d.longitude,
      photo: d.driverPhoto ?? null,
      carPhoto: d.carPhoto ?? null,
      color: d.carColor ?? null,
    });
  }
  return out;
}
